<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import clubService from '../services/club.service'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Personal info
const firstName = ref('')
const lastName = ref('')
const phone = ref('')

// Club basic info
const clubName = ref('')
const clubShortName = ref('')
const clubNickname = ref('')
const clubNip = ref('')
const vatUe = ref(false)
const vatPrefix = ref('')
const clubLogo = ref<File | null>(null)
const logoPreview = ref<string | null>(null)

// Address
const street = ref('')
const buildingNumber = ref('')
const apartmentNumber = ref('')
const city = ref('')
const postalCode = ref('')
const country = ref('PL')

const countryCodes = [
  'AU', 'AT', 'BE', 'BR', 'BG', 'CA', 'HR', 'CY', 'CZ', 'DK',
  'EE', 'FI', 'FR', 'DE', 'GI', 'GR', 'HK', 'HU', 'IN', 'IE',
  'IT', 'JP', 'LV', 'LI', 'LT', 'LU', 'MY', 'MT', 'MX', 'NL',
  'NZ', 'NO', 'PL', 'PT', 'RO', 'SG', 'SK', 'SI', 'ES', 'SE',
  'CH', 'TH', 'AE', 'GB', 'US'
]

// Contact
const email = ref('')
const clubPhone = ref('')
const website = ref('')

// Social media
const facebookUrl = ref('')
const instagramUrl = ref('')
const tiktokUrl = ref('')

// Timezone
const timezone = ref('Europe/Warsaw')

const timezones = [
  { value: 'Europe/Warsaw', label: 'Europe/Warsaw (GMT+1/+2)' },
  { value: 'Europe/London', label: 'Europe/London (GMT+0/+1)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (GMT+1/+2)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (GMT+1/+2)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (GMT+1/+2)' },
  { value: 'Europe/Rome', label: 'Europe/Rome (GMT+1/+2)' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (GMT+1/+2)' },
  { value: 'Europe/Brussels', label: 'Europe/Brussels (GMT+1/+2)' },
  { value: 'Europe/Vienna', label: 'Europe/Vienna (GMT+1/+2)' },
  { value: 'Europe/Prague', label: 'Europe/Prague (GMT+1/+2)' },
  { value: 'Europe/Budapest', label: 'Europe/Budapest (GMT+1/+2)' },
  { value: 'Europe/Bucharest', label: 'Europe/Bucharest (GMT+2/+3)' },
  { value: 'Europe/Athens', label: 'Europe/Athens (GMT+2/+3)' },
  { value: 'Europe/Helsinki', label: 'Europe/Helsinki (GMT+2/+3)' },
  { value: 'Europe/Stockholm', label: 'Europe/Stockholm (GMT+1/+2)' },
  { value: 'Europe/Oslo', label: 'Europe/Oslo (GMT+1/+2)' },
  { value: 'Europe/Copenhagen', label: 'Europe/Copenhagen (GMT+1/+2)' },
  { value: 'Europe/Dublin', label: 'Europe/Dublin (GMT+0/+1)' },
  { value: 'Europe/Lisbon', label: 'Europe/Lisbon (GMT+0/+1)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (GMT+3)' },
  { value: 'Europe/Istanbul', label: 'Europe/Istanbul (GMT+3)' },
  { value: 'Europe/Kiev', label: 'Europe/Kiev (GMT+2/+3)' },
  { value: 'America/New_York', label: 'America/New_York (GMT-5/-4)' },
  { value: 'America/Chicago', label: 'America/Chicago (GMT-6/-5)' },
  { value: 'America/Denver', label: 'America/Denver (GMT-7/-6)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (GMT-8/-7)' },
  { value: 'America/Toronto', label: 'America/Toronto (GMT-5/-4)' },
  { value: 'America/Vancouver', label: 'America/Vancouver (GMT-8/-7)' },
  { value: 'America/Mexico_City', label: 'America/Mexico_City (GMT-6/-5)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (GMT-3/-2)' },
  { value: 'America/Buenos_Aires', label: 'America/Buenos_Aires (GMT-3)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (GMT+9)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (GMT+8)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (GMT+8)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (GMT+8)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GMT+4)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (GMT+5:30)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (GMT+10/+11)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (GMT+10/+11)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (GMT+12/+13)' },
  { value: 'Africa/Cairo', label: 'Africa/Cairo (GMT+2)' },
  { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (GMT+2)' }
]

const loading = ref(false)
const validationErrors = ref<Record<string, string[]>>({})

const handleLogoChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]

  if (file) {
    clubLogo.value = file

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      logoPreview.value = e.target?.result as string
    }
    reader.readAsDataURL(file)
  }
}

const removeLogo = () => {
  clubLogo.value = null
  logoPreview.value = null
  const input = document.getElementById('logo') as HTMLInputElement
  if (input) {
    input.value = ''
  }
}

const handleSubmit = async () => {
  loading.value = true
  validationErrors.value = {}

  try {
    const formData = new FormData()

    // Personal info
    formData.append('first_name', firstName.value)
    formData.append('last_name', lastName.value)
    if (phone.value) formData.append('phone', phone.value)

    // Club basic info
    formData.append('name', clubName.value)
    formData.append('short_name', clubShortName.value)
    if (clubNickname.value) formData.append('nickname', clubNickname.value)
    if (clubNip.value) formData.append('nip', clubNip.value)
    formData.append('vat_ue', vatUe.value ? '1' : '0')
    if (vatUe.value && vatPrefix.value) formData.append('vat_prefix', vatPrefix.value)
    if (clubLogo.value) formData.append('logo', clubLogo.value)

    // Address
    if (street.value) formData.append('street', street.value)
    if (buildingNumber.value) formData.append('building_number', buildingNumber.value)
    if (apartmentNumber.value) formData.append('apartment_number', apartmentNumber.value)
    if (city.value) formData.append('city', city.value)
    if (postalCode.value) formData.append('postal_code', postalCode.value)
    formData.append('country', country.value)

    // Contact
    if (email.value) formData.append('email', email.value)
    if (clubPhone.value) formData.append('phone', clubPhone.value)
    if (website.value) formData.append('website', website.value)

    // Social media
    if (facebookUrl.value) formData.append('facebook_url', facebookUrl.value)
    if (instagramUrl.value) formData.append('instagram_url', instagramUrl.value)
    if (tiktokUrl.value) formData.append('tiktok_url', tiktokUrl.value)

    // Timezone
    formData.append('timezone', timezone.value)

    const response = await clubService.createClub(formData)

    toastStore.success(t('club.created'))

    // Refresh init data to load new club
    const userUuid = authStore.user?.uuid
    if (userUuid) {
      await authStore.initUserData(userUuid)
    }

    // Redirect to home (which will redirect to dashboard based on role)
    router.push('/')
  } catch (err: any) {
    console.error('Create club error:', err)

    if (err.response?.data?.errors) {
      validationErrors.value = err.response.data.errors
    } else {
      toastStore.error(err.response?.data?.message || t('club.errorCreating'))
    }
  } finally {
    loading.value = false
  }
}

const getFieldError = (fieldName: string): string | null => {
  const errors = validationErrors.value[fieldName]
  return errors && errors.length > 0 ? errors[0] : null
}

const hasFieldError = (fieldName: string): boolean => {
  return !!validationErrors.value[fieldName]
}

const handleCancel = () => {
  router.push('/choose-profile')
}
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="max-w-4xl mx-auto px-4 py-4">
      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('club.registerClub') }}</h1>
        <p class="text-sm text-gray-600 mt-1">{{ t('club.registerClubSubtitle') }}</p>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
        <!-- Personal Information -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
            {{ t('club.personalInfo') }}
          </h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <!-- First Name -->
            <div>
              <label for="first_name" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('club.firstName') }} <span class="text-red-600">*</span>
              </label>
              <input
                v-model="firstName"
                type="text"
                id="first_name"
                required
                :class="[
                  'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                  hasFieldError('first_name') ? 'border-red-500' : 'border-gray-300'
                ]"
              />
              <p v-if="getFieldError('first_name')" class="text-xs text-red-600 mt-1">
                {{ getFieldError('first_name') }}
              </p>
            </div>

            <!-- Last Name -->
            <div>
              <label for="last_name" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('club.lastName') }} <span class="text-red-600">*</span>
              </label>
              <input
                v-model="lastName"
                type="text"
                id="last_name"
                required
                :class="[
                  'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                  hasFieldError('last_name') ? 'border-red-500' : 'border-gray-300'
                ]"
              />
              <p v-if="getFieldError('last_name')" class="text-xs text-red-600 mt-1">
                {{ getFieldError('last_name') }}
              </p>
            </div>
          </div>

          <!-- Phone -->
          <div class="mt-3">
            <label for="person_phone" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.phone') }}</label>
            <input
              v-model="phone"
              type="text"
              id="person_phone"
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('phone') ? 'border-red-500' : 'border-gray-300'
              ]"
            />
            <p v-if="getFieldError('phone')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('phone') }}
            </p>
          </div>
        </div>

        <!-- Club Basic Information -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
            {{ t('club.basicInfo') }}
          </h2>

          <!-- Club Name -->
          <div class="mb-3">
            <label for="club_name" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('club.name') }} <span class="text-red-600">*</span>
            </label>
            <input
              v-model="clubName"
              type="text"
              id="club_name"
              required
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('name') ? 'border-red-500' : 'border-gray-300'
              ]"
            />
            <p v-if="getFieldError('name')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('name') }}
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <!-- Short Name -->
            <div>
              <label for="short_name" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('club.shortName') }} <span class="text-red-600">*</span>
              </label>
              <input
                v-model="clubShortName"
                type="text"
                id="short_name"
                required
                :class="[
                  'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                  hasFieldError('short_name') ? 'border-red-500' : 'border-gray-300'
                ]"
              />
              <p v-if="getFieldError('short_name')" class="text-xs text-red-600 mt-1">
                {{ getFieldError('short_name') }}
              </p>
            </div>

            <!-- Nickname -->
            <div>
              <label for="nickname" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.nickname') }}</label>
              <input
                v-model="clubNickname"
                type="text"
                id="nickname"
                :class="[
                  'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                  hasFieldError('nickname') ? 'border-red-500' : 'border-gray-300'
                ]"
              />
              <p v-if="getFieldError('nickname')" class="text-xs text-red-600 mt-1">
                {{ getFieldError('nickname') }}
              </p>
            </div>
          </div>

          <!-- NIP -->
          <div class="mt-3">
            <label for="nip" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.nip') }}</label>
            <input
              v-model="clubNip"
              type="text"
              id="nip"
              maxlength="10"
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('nip') ? 'border-red-500' : 'border-gray-300'
              ]"
              placeholder="1234567890"
            />
            <p v-if="getFieldError('nip')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('nip') }}
            </p>
          </div>

          <!-- VAT UE -->
          <div class="mt-3">
            <label class="inline-flex items-center cursor-pointer">
              <input
                v-model="vatUe"
                type="checkbox"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span class="ml-2 text-sm font-medium text-gray-700">{{ t('club.vatUe') }}</span>
            </label>
          </div>

          <!-- VAT Prefix -->
          <div v-if="vatUe" class="mt-3">
            <label for="vat_prefix" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.vatPrefix') }}</label>
            <input
              v-model="vatPrefix"
              type="text"
              id="vat_prefix"
              maxlength="10"
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('vat_prefix') ? 'border-red-500' : 'border-gray-300'
              ]"
              :placeholder="t('club.countryCodePlaceholder')"
            />
            <p v-if="getFieldError('vat_prefix')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('vat_prefix') }}
            </p>
          </div>

          <!-- Logo Upload -->
          <div class="mt-3">
            <label for="logo" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.logo') }}</label>
            <input
              type="file"
              id="logo"
              @change="handleLogoChange"
              accept="image/jpeg,image/png,image/jpg"
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('logo') ? 'border-red-500' : 'border-gray-300'
              ]"
            />
            <p class="text-xs text-gray-500 mt-1">{{ t('club.logoHint') }}</p>
            <p v-if="getFieldError('logo')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('logo') }}
            </p>

            <!-- Logo Preview -->
            <div v-if="logoPreview" class="mt-3 relative inline-block">
              <img :src="logoPreview" alt="Logo preview" class="w-32 h-32 object-cover border-2 border-gray-300 rounded" />
              <button
                type="button"
                @click="removeLogo"
                class="absolute top-0 right-0 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700 transform translate-x-2 -translate-y-2"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <!-- Address -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('club.address') }}</h2>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <!-- Street -->
            <div class="md:col-span-2">
              <label for="street" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.street') }}</label>
              <input
                v-model="street"
                type="text"
                id="street"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              />
            </div>

            <!-- Building Number -->
            <div>
              <label for="building_number" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.buildingNumber') }}</label>
              <input
                v-model="buildingNumber"
                type="text"
                id="building_number"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <!-- Apartment Number -->
            <div>
              <label for="apartment_number" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.apartmentNumber') }}</label>
              <input
                v-model="apartmentNumber"
                type="text"
                id="apartment_number"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              />
            </div>

            <!-- City -->
            <div>
              <label for="city" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.city') }}</label>
              <input
                v-model="city"
                type="text"
                id="city"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              />
            </div>

            <!-- Postal Code -->
            <div>
              <label for="postal_code" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.postalCode') }}</label>
              <input
                v-model="postalCode"
                type="text"
                id="postal_code"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
                placeholder="00-000"
              />
            </div>
          </div>

          <!-- Country -->
          <div class="mt-3">
            <label for="country" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.country') }}</label>
            <select
              id="country"
              v-model="country"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
            >
              <option v-for="code in countryCodes" :key="code" :value="code">
                {{ t(`countries.${code}`) }}
              </option>
            </select>
          </div>
        </div>

        <!-- Contact Information -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('club.contact') }}</h2>

          <!-- Email -->
          <div class="mb-3">
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.email') }}</label>
            <input
              v-model="email"
              type="email"
              id="email"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
            />
          </div>

          <!-- Phone -->
          <div class="mb-3">
            <label for="club_phone" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.phone') }}</label>
            <input
              v-model="clubPhone"
              type="text"
              id="club_phone"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
            />
          </div>

          <!-- Website -->
          <div class="mb-3">
            <label for="website" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.website') }}</label>
            <input
              v-model="website"
              type="url"
              id="website"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="https://example.com"
            />
          </div>
        </div>

        <!-- Social Media -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('club.socialMedia') }}</h2>

          <!-- Facebook URL -->
          <div class="mb-3">
            <label for="facebook_url" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.facebookUrl') }}</label>
            <input
              v-model="facebookUrl"
              type="url"
              id="facebook_url"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="https://facebook.com/yourclub"
            />
          </div>

          <!-- Instagram URL -->
          <div class="mb-3">
            <label for="instagram_url" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.instagramUrl') }}</label>
            <input
              v-model="instagramUrl"
              type="url"
              id="instagram_url"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="https://instagram.com/yourclub"
            />
          </div>

          <!-- TikTok URL -->
          <div class="mb-3">
            <label for="tiktok_url" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.tiktokUrl') }}</label>
            <input
              v-model="tiktokUrl"
              type="url"
              id="tiktok_url"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              placeholder="https://tiktok.com/@yourclub"
            />
          </div>

          <!-- Timezone -->
          <div class="mb-3">
            <label for="timezone" class="block text-sm font-medium text-gray-700 mb-1">{{ t('club.timezone') }}</label>
            <select
              id="timezone"
              v-model="timezone"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
            >
              <option v-for="tz in timezones" :key="tz.value" :value="tz.value">
                {{ tz.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Submit Section -->
        <div class="pt-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            @click="handleCancel"
            class="py-2 px-4 bg-gray-100 text-gray-700 font-medium rounded hover:bg-gray-200 border border-gray-300"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            :disabled="loading"
            class="py-2 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loading ? t('club.creating') : t('club.createClub') }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>
